import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// IndexedDB Setup
const DB_NAME = 'gonkleNotes';
const DB_VERSION = 1;
const STORE_NAME = 'notes';

const openDB = () => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);

        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: 'id' });
            }
        };
    });
};

const saveNote = async (note) => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.put({ id: 'userNote', content: note, timestamp: Date.now() });

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
};

const loadNote = async () => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get('userNote');

        request.onsuccess = () => resolve(request.result?.content || '');
        request.onerror = () => reject(request.error);
    });
};

const NotesModal = ({ onClose }) => {
    const { t } = useTranslation();
    const [note, setNote] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);

    useEffect(() => {
        const loadInitialNote = async () => {
            try {
                const savedNote = await loadNote();
                setNote(savedNote);
            } catch (error) {
                console.error('Error loading note:', error);
            }
        };
        loadInitialNote();
    }, []);

    useEffect(() => {
        if (note === '') return;

        const saveTimer = setTimeout(async () => {
            setIsSaving(true);
            try {
                await saveNote(note);
                setLastSaved(new Date());
            } catch (error) {
                console.error('Error saving note:', error);
            } finally {
                setIsSaving(false);
            }
        }, 1000); // Auto-save 1s nach letzter Änderung

        return () => clearTimeout(saveTimer);
    }, [note]);

    return (
        <div className="modal-backdrop" onClick={onClose} style={{ zIndex: 1300 }}>
            <motion.div
                className="modal-content"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 150, damping: 20 }}
                onClick={e => e.stopPropagation()}
                style={{ maxWidth: '500px', width: '90%' }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h2 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: '1.8em' }}>
                        📝 {t('notes_title') || 'My Notes'}
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            fontSize: '1.5em',
                            cursor: 'pointer',
                            padding: '5px 10px'
                        }}
                    >
                        ✕
                    </button>
                </div>

                <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={t('notes_placeholder') || 'Emma has a better volleyball, leave mine at home...'}
                    style={{
                        width: '100%',
                        minHeight: '300px',
                        maxHeight: '60vh',
                        resize: 'vertical',
                        fontFamily: 'var(--font-body)',
                        fontSize: '1em',
                        padding: '15px',
                        boxSizing: 'border-box'
                    }}
                />

                <div style={{ marginTop: '10px', fontSize: '0.85em', opacity: 0.7, textAlign: 'right' }}>
                    {isSaving && (t('notes_saving') || 'Saving...')}
                    {!isSaving && lastSaved && (
                        <>
                            ✓ {t('notes_saved') || 'Saved'} {lastSaved.toLocaleTimeString()}
                        </>
                    )}
                </div>
            </motion.div>
        </div>
    );
};

export default React.memo(NotesModal);
